<template>
  <div class="vesting-info-form">
    <section class="vif-body">

      <!-- Employee Profile Header -->
      <div class="vif-profile">
        <div class="vif-profile__info">
          <img
            v-if="employee.employee?.image?.length > 0"
            :src="employee.employee?.image"
            :alt="employee.employee?.fname"
            class="vif-profile__avatar"
          />
          <div v-else class="vif-profile__avatar-placeholder">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div>
            <p class="vif-profile__name">{{ employee.invitation.name }}</p>
            <p class="vif-profile__role">{{ employee.role }}</p>
          </div>
        </div>
        <span class="vif-badge" :class="`vif-badge--${status.toLowerCase()}`">{{ status }}</span>
      </div>

      <!-- Vesting Progress Banner -->
      <div class="vif-progress-banner">
        <div class="vif-progress-banner__stat">
          <p class="vif-progress-banner__value">
            <template v-if="employee.status === 'Pending' || employee.status === 'Terminated' || status === 'Rejected'">
              0.00%
            </template>
            <template v-else>
              {{ ((vestingSchedule / 1) * 100).toFixed(2) }}%
            </template>
          </p>
          <p class="vif-progress-banner__label">Percentage vested</p>
          <ProgressBar
            class="vif-progress-bar"
            :currentNumber="employee.status === 'Pending' || status === 'Rejected' ? 0 : vestingSchedule"
            :totalValue="1"
          />
        </div>
        <div class="vif-progress-banner__divider"></div>
        <div class="vif-progress-banner__stat">
          <p class="vif-progress-banner__value">
            {{ formatAmountToDollar(employee.employee?.exercised_amount || 0, 2, "USD") }}
          </p>
          <p class="vif-progress-banner__label">Amount exercised</p>
        </div>
      </div>

      <!-- Vesting Details Grid -->
      <div class="vif-details-grid">
        <div class="vif-detail-card">
          <p class="vif-detail-card__label">Duration</p>
          <p class="vif-detail-card__value">
            {{ employee.vesting_duration }}
            {{ employee.vesting_duration > 1 ? "years" : "year" }}
          </p>
        </div>
        <div class="vif-detail-card">
          <p class="vif-detail-card__label">Start date</p>
          <p class="vif-detail-card__value">
            {{ formatISODate(employee.start_date || employee.createdAt) }}
          </p>
        </div>
        <div class="vif-detail-card">
          <p class="vif-detail-card__label">Vesting schedule</p>
          <p class="vif-detail-card__value">{{ vestingFrequency }}</p>
        </div>
        <div class="vif-detail-card">
          <p class="vif-detail-card__label">Cliff period</p>
          <p class="vif-detail-card__value">
            {{ employee.cliff_period }}
            {{ employee.cliff_period > 1 ? "months" : "month" }}
          </p>
        </div>
        <div class="vif-detail-card">
          <p class="vif-detail-card__label">
            {{ employee.strike_price > 0 ? "Strike price" : "Market price" }}
          </p>
          <p class="vif-detail-card__value">
            {{ employee.strike_price > 0
              ? formatAmountToDollar(employee.strike_price, 2, "USD")
              : formatAmountToDollar(marketPrice, 2, "USD") }}
          </p>
        </div>
        <div class="vif-detail-card">
          <p class="vif-detail-card__label">Allocated shares</p>
          <p class="vif-detail-card__value">{{ convertToLocale(employee.allocated_shares) }}</p>
        </div>
      </div>

      <!-- Documents -->
      <div v-if="employee.documents?.length" class="vif-documents">
        <p class="vif-section-label">Documents</p>
        <div class="vif-documents__grid">
          <div v-for="(document, index) in employee.documents" :key="index">
            <a :href="document.url" target="_blank" rel="noopener noreferrer" class="vif-doc-card">
              <div class="vif-doc-card__icon">
                <img src="@/assets/img/doc-file.svg" alt="document" />
              </div>
              <p class="vif-doc-card__name">{{ document.name }}</p>
              <p class="vif-doc-card__type">PDF</p>
            </a>
          </div>
        </div>
      </div>


      <!-- Exercise Timeline Notice -->
      <p
        v-if="employee.exercise_timeline && status !== 'Rejected'"
        class="vif-timeline-notice"
      >
        Exercise timeline: {{ formatISODate(exerciseTimeline.start) }} — {{ formatISODate(exerciseTimeline.end) }}
      </p>

      <!-- Rejection Notice -->
      <div v-if="status === 'Rejected'" class="vif-rejection-notice">
        <div class="vif-rejection-notice__header">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
            <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          This invite has been declined.
        </div>
        <p class="vif-rejection-notice__reason-label">Reason</p>
        <p class="vif-rejection-notice__reason">{{ employee.decline_reason }}</p>
      </div>

      <!-- Action Buttons -->
      <div v-if="employee.status === 'Pending'" class="vif-actions">
        <Btn
          title="Edit Invite"
          @click="$router.push({ name: 'InviteEmployee', query: { id: employee._id } })"
        />
        <Btn title="Cancel Invitation" @click="terminateInvite" />
      </div>
      <div v-if="employee.employee?.status === 'Accepted'" class="vif-actions">
        <Btn title="Terminate ESOP" @click="terminateEsop" />
      </div>

    </section>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import ProgressBar from "@/components/general/ProgressBar";
  import Btn from "@/components/general/BtnComponent";
  import {
    formatAmountToDollar,
    formatISODate,
    convertToLocale,
  } from "@/utils/helpers.js";

  export default {
    name: "VestingInfoForm",
    data() {
      return {
        exerciseTimelineTooltip: "It means yes",
      };
    },

    emits: ["reload-data", "top-up"],

    components: {
      ProgressBar,
      Btn,
    },

    props: {
      employee: {
        type: Object,
        required: true,
        default: () => {},
      },
      status: {
        type: String,
        required: true,
        default: () => "",
      },
      vestingSchedule: {
        type: Number,
        required: true,
        default: () => 0,
      },
      vestingFrequency: {
        type: String,
        required: true,
        default: () => "",
      },
      marketPrice: {
        type: Number,
        required: true,
        default: () => 0,
      },
    },
    computed: {
      exerciseTimeline() {
        let exerciseTimeline = {
          start: null,
          end: null,
        };
        const vestingStartDate = new Date(this.employee.start_date);
        const duration = this.employee.vesting_duration * 365;
        const vestingEndDate = new Date(
          vestingStartDate.setDate(vestingStartDate.getDate() + duration)
        );
        const exerciseTimelineStart = new Date(vestingEndDate);
        exerciseTimeline.start = exerciseTimelineStart;
        const exerciseTimelineDuration =
          this.employee && (this.employee.exercise_timeline / 12) * 365;
        const exerciseTimelineEnd = new Date(
          new Date(vestingEndDate).setDate(
            new Date(vestingEndDate).getDate() + exerciseTimelineDuration
          )
        );
        exerciseTimeline.end = exerciseTimelineEnd;
        return exerciseTimeline;
      },
    },
    methods: {
      ...mapActions(["showToast"]),
      ...mapActions("Esop", ["cancelInvite", "terminateEmployeeEsop"]),

      formatAmountToDollar,
      convertToLocale,
      formatISODate,

      async terminateInvite() {
        const id = this.employee._id;
        try {
          const response = await this.cancelInvite(id);
          const { message } = response.data;
          this.showToast({
            description: message,
            display: true,
            type: "success",
          });
          this.$router.push({ name: "Employees" });
          this.$emit("reload-data");
        } catch (error) {
          return error;
        }
      },
      async terminateEsop() {
        const id = this.employee.employee._id;
        try {
          const response = await this.terminateEmployeeEsop(id);
          const { message } = response.data;
          this.showToast({
            description: message,
            display: true,
            type: "success",
          });
          this.$router.push({ name: "Employees" });
          this.$emit("reload-data");
        } catch (error) {
          return error;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .vesting-info-form {
    font-family: 'DM Sans', sans-serif;
  }

  .vif-body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  // Employee Profile Header
  .vif-profile {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    &__info {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    &__avatar {
      width: 44px;
      height: 44px;
      border-radius: 10px;
      object-fit: cover;
      border: 1px solid #E2E8F0;
    }

    &__avatar-placeholder {
      width: 44px;
      height: 44px;
      border-radius: 10px;
      background: #EEF2FF;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #4D84FF;
      flex-shrink: 0;
    }

    &__name {
      font-size: 15px;
      font-weight: 500;
      color: #0F172A;
      margin: 0 0 2px;
    }

    &__role {
      font-size: 13px;
      color: #64748B;
      margin: 0;
      text-transform: capitalize;
    }
  }

  // Status badge
  .vif-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;

    &--contract\ signed,
    &--accepted,
    &--esop {
      color: #27AE60;
      background: #E9F7EF;
    }

    &--pending {
      color: #E2B93B;
      background: #FCF9ED;
    }

    &--terminated,
    &--rejected {
      color: #EB5757;
      background: #F5D5CB;
    }

    &--rsu {
      color: #4D84FF;
      background: #E5EDFF;
    }
  }

  // Progress Banner
  .vif-progress-banner {
    background: linear-gradient(135deg, #001140 0%, #0D2260 100%);
    border-radius: 12px;
    padding: 20px 24px;
    display: flex;
    align-items: center;
    gap: 0;

    &__stat {
      flex: 1;
    }

    &__divider {
      width: 1px;
      height: 48px;
      background: rgba(255, 255, 255, 0.15);
      margin: 0 24px;
      flex-shrink: 0;
    }

    &__value {
      font-size: 20px;
      font-weight: 500;
      color: #ffffff;
      margin: 0 0 4px;
    }

    &__label {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.6);
      margin: 0;
    }
  }

  .vif-progress-bar {
    margin-top: 10px;
    max-width: 140px;
  }

  // Details grid
  .vif-details-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .vif-detail-card {
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 14px 16px;

    &__label {
      font-size: 12px;
      color: #64748B;
      margin: 0 0 6px;
    }

    &__value {
      font-size: 16px;
      font-weight: 500;
      color: #0F172A;
      margin: 0;
    }
  }

  // Section label
  .vif-section-label {
    font-size: 13px;
    font-weight: 500;
    color: #64748B;
    margin: 0 0 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  // Documents
  .vif-documents {
    &__grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }
  }

  .vif-doc-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 16px 12px;
    text-decoration: none;
    transition: border-color 0.2s ease, background 0.2s ease;

    &:hover {
      border-color: #4D84FF;
      background: #F0F4FF;
    }

    &__icon {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 28px;
        height: 28px;
        object-fit: contain;
      }
    }

    &__name {
      font-size: 12px;
      font-weight: 500;
      color: #0F172A;
      margin: 0;
      text-align: center;
      word-break: break-word;
    }

    &__type {
      font-size: 11px;
      color: #94A3B8;
      margin: 0;
    }
  }

  // Exercise timeline
  .vif-timeline-notice {
    font-size: 13px;
    color: #4C5B90;
    text-align: center;
    margin: 0;
    padding: 10px 16px;
    background: #EEF2FF;
    border-radius: 8px;
  }

  // Rejection notice
  .vif-rejection-notice {
    background: #FEF2F2;
    border: 1px solid #FECACA;
    border-radius: 10px;
    padding: 16px 20px;

    &__header {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 500;
      color: #EF4444;
      margin-bottom: 12px;
    }

    &__reason-label {
      font-size: 12px;
      font-weight: 500;
      color: #64748B;
      margin: 0 0 4px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    &__reason {
      font-size: 14px;
      color: #0F172A;
      margin: 0;
    }
  }

  // Action buttons row
  .vif-actions {
    display: flex;
    gap: 12px;
    padding-top: 4px;
  }
</style>
